import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import TsKayitShell from '../components/TsKayitShell';
import {
  checkTsKayitKullaniciAdi,
  fetchTsKayitForm,
  fetchTsKayitOturum,
  searchTsKayitTeknikServisler,
  sendTsKayitNot,
  submitTsKayitBasvuru,
} from '../services/tsKayitBasvuruService';
import './TsKayitPublicPage.css';

/*
 * Teknik servis kayıt linki (/s/:token)
 * 1) Servis seçimi  2) Kullanıcı adı  3) Şifre + tekrar  4) Onay bekleniyor (+ yöneticiye yaz)
 * Kurallar backend/constants/tsKayitBasvuru.js ile aynı tutulmalı.
 */

const OTURUM_STORAGE_PREFIX = 'ts-kayit-oturum-';
const LEGACY_SUBMIT_PREFIX = 'ts-kayit-submitted-';

const KULLANICI_ADI_MIN = 6;
const KULLANICI_ADI_MAX = 30;
const TURKCE_KARAKTER = /[çğıöşüÇĞİÖŞÜ]/;

const KULLANICI_ADI_KURALLARI = [
  { id: 'uzunluk', label: `${KULLANICI_ADI_MIN}-${KULLANICI_ADI_MAX} karakter`, test: (v) => v.length >= KULLANICI_ADI_MIN && v.length <= KULLANICI_ADI_MAX },
  { id: 'karakter', label: 'Yalnızca harf (a-z), rakam (0-9) ve alt çizgi ( _ )', test: (v) => v.length > 0 && /^[a-z0-9_]+$/.test(v) },
  { id: 'turkce', label: 'Türkçe karakter yok (ç, ğ, ı, ö, ş, ü)', test: (v) => v.length > 0 && !TURKCE_KARAKTER.test(v) },
  { id: 'bosluk', label: 'Boşluk yok', test: (v) => v.length > 0 && !/\s/.test(v) },
];

const SIFRE_KURALLARI = [
  { id: 'uzunluk', label: '8-32 karakter', test: (v) => v.length >= 8 && v.length <= 32 },
  { id: 'buyuk', label: 'En az 1 büyük harf (A-Z)', test: (v) => /[A-Z]/.test(v) },
  { id: 'kucuk', label: 'En az 1 küçük harf (a-z)', test: (v) => /[a-z]/.test(v) },
  { id: 'rakam', label: 'En az 1 rakam (0-9)', test: (v) => /\d/.test(v) },
  { id: 'turkce', label: 'Türkçe karakter ve boşluk yok', test: (v) => v.length > 0 && /^[\x21-\x7E]+$/.test(v) },
];

const ADIM_BASLIKLARI = ['Servisinizi seçin', 'Kullanıcı adı belirleyin', 'Şifre belirleyin'];

function kurallariDegerlendir(kurallar, value) {
  return kurallar.map((k) => ({ ...k, passed: k.test(value) }));
}

function storageGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function storageSet(key, value) {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
}
function storageRemove(key) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

function getStoredOturum(token) {
  const key = `${OTURUM_STORAGE_PREFIX}${token}`;
  const raw = storageGet(key);
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.basvuruId) return parsed;
    } catch { /* ignore */ }
  }
  const legacy = storageGet(`${LEGACY_SUBMIT_PREFIX}${token}`);
  if (legacy) {
    try {
      const parsed = JSON.parse(legacy);
      const basvuruId = parsed.basvuru?.id || parsed.basvuru?.basvuruId;
      if (basvuruId) {
        const oturum = { basvuruId, savedAt: Date.now() };
        storageSet(key, JSON.stringify(oturum));
        storageRemove(`${LEGACY_SUBMIT_PREFIX}${token}`);
        return oturum;
      }
    } catch { /* ignore */ }
  }
  return null;
}

function saveOturum(token, basvuruId) {
  storageSet(`${OTURUM_STORAGE_PREFIX}${token}`, JSON.stringify({ basvuruId, savedAt: Date.now() }));
}

function clearOturum(token) {
  storageRemove(`${OTURUM_STORAGE_PREFIX}${token}`);
  storageRemove(`${LEGACY_SUBMIT_PREFIX}${token}`);
}

function normalizeAra(value) {
  return String(value || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i').replace(/i̇/g, 'i')
    .replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
    .trim();
}

function KuralListesi({ kurallar, dolu }) {
  return (
    <ul className="ts-kayit-kurallar">
      {kurallar.map((k) => (
        <li key={k.id} className={k.passed ? 'passed' : (dolu ? 'failed' : '')}>
          <i className={`fas ${k.passed ? 'fa-check-circle' : (dolu ? 'fa-times-circle' : 'fa-circle')}`} aria-hidden="true" />
          <span>{k.label}</span>
        </li>
      ))}
    </ul>
  );
}

const DURUM_KART_STIL = {
  onay_bekliyor: { icon: 'fa-clock', className: 'bekliyor' },
  onaylandi: { icon: 'fa-check-circle', className: 'onaylandi' },
  reddedildi: { icon: 'fa-times-circle', className: 'reddedildi' },
};

function DurumKarti({ durumKarti, basvuru }) {
  const durum = durumKarti?.durum || 'onay_bekliyor';
  const stil = DURUM_KART_STIL[durum] || DURUM_KART_STIL.onay_bekliyor;
  return (
    <div className={`ts-kayit-durum-kart ${stil.className}`}>
      <i className={`fas ${stil.icon}`} />
      {durum === 'onay_bekliyor' && (
        <>
          <h2>İşleminiz tamamlandı</h2>
          <p className="ts-kayit-durum-alt">Başvurunuz onay bekliyor.</p>
          <p className="ts-kayit-durum-aciklama">
            Onaylandığında belirlediğiniz kullanıcı adı ve şifre ile uygulamaya giriş yapabilirsiniz.
          </p>
        </>
      )}
      {durum === 'onaylandi' && (
        <>
          <h2>Başvurunuz onaylandı</h2>
          <p className="ts-kayit-durum-aciklama">
            Belirlediğiniz kullanıcı adı ve şifre ile uygulamaya giriş yapabilirsiniz.
          </p>
        </>
      )}
      {durum === 'reddedildi' && <h2>{durumKarti?.mesaj || 'Başvurunuz reddedildi'}</h2>}
      {basvuru?.kullaniciAdi && (
        <div className="ts-kayit-ozet">
          <div><span>Kullanıcı adı</span><strong>{basvuru.kullaniciAdi}</strong></div>
          {basvuru.techService?.ad && (
            <div><span>Servis</span><strong>{basvuru.techService.ad}</strong></div>
          )}
        </div>
      )}
    </div>
  );
}

function YoneticiyeYaz({ token, baglam, onKapat }) {
  const [adSoyad, setAdSoyad] = useState('');
  const [iletisim, setIletisim] = useState('');
  const [mesaj, setMesaj] = useState('');
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [hata, setHata] = useState('');
  const [gonderildi, setGonderildi] = useState(false);

  const gonder = async () => {
    if (mesaj.trim().length < 5) {
      setHata('Lütfen sorunu birkaç kelimeyle yazın');
      return;
    }
    setHata('');
    setGonderiliyor(true);
    try {
      await sendTsKayitNot(token, {
        mesaj: mesaj.trim(),
        adSoyad: adSoyad.trim(),
        iletisim: iletisim.trim(),
        basvuruId: baglam?.basvuruId || undefined,
        techServiceId: baglam?.techServiceId || undefined,
        kullaniciAdi: baglam?.kullaniciAdi || undefined,
      });
      setGonderildi(true);
    } catch (err) {
      setHata(err?.message || 'Mesaj gönderilemedi');
    } finally {
      setGonderiliyor(false);
    }
  };

  if (gonderildi) {
    return (
      <div className="ts-kayit-yardim-kutu">
        <div className="ts-kayit-hint ok ts-kayit-yardim-ok">
          <i className="fas fa-check-circle" /> Mesajınız sistem yöneticisine iletildi.
        </div>
        <div className="ts-kayit-actions">
          <button type="button" className="ts-kayit-btn-secondary" onClick={onKapat}>Kapat</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ts-kayit-yardim-kutu">
      <h3>Sistem yöneticisine yazın</h3>
      <div className="ts-kayit-field">
        <label htmlFor="ts-not-ad">Adınız soyadınız (isteğe bağlı)</label>
        <input id="ts-not-ad" type="text" maxLength={100} value={adSoyad} onChange={(e) => setAdSoyad(e.target.value)} />
      </div>
      <div className="ts-kayit-field">
        <label htmlFor="ts-not-iletisim">Telefon (isteğe bağlı)</label>
        <input id="ts-not-iletisim" type="tel" maxLength={100} value={iletisim} onChange={(e) => setIletisim(e.target.value)} />
      </div>
      <div className="ts-kayit-field">
        <label htmlFor="ts-not-mesaj">Mesajınız</label>
        <textarea
          id="ts-not-mesaj"
          rows={4}
          maxLength={1000}
          value={mesaj}
          onChange={(e) => setMesaj(e.target.value)}
          placeholder="Yaşadığınız sorunu kısaca yazın"
        />
        <div className="ts-kayit-hint">{mesaj.length}/1000</div>
      </div>
      {hata && <div className="ts-kayit-hint err">{hata}</div>}
      <div className="ts-kayit-actions">
        <button type="button" className="ts-kayit-btn-secondary" onClick={onKapat} disabled={gonderiliyor}>Vazgeç</button>
        <button type="button" className="ts-kayit-btn-primary" onClick={gonder} disabled={gonderiliyor}>
          {gonderiliyor ? 'Gönderiliyor...' : 'Gönder'}
        </button>
      </div>
    </div>
  );
}

function YardimAlani({ token, baglam, metin = 'Bir sorun olduğunu düşünüyorsanız sistem yöneticisine haber verin.' }) {
  const [acik, setAcik] = useState(false);
  if (acik) return <YoneticiyeYaz token={token} baglam={baglam} onKapat={() => setAcik(false)} />;
  return (
    <div className="ts-kayit-yardim">
      <span>{metin}</span>
      <button type="button" className="ts-kayit-link-btn" onClick={() => setAcik(true)}>
        <i className="fas fa-envelope" /> Sistem yöneticisine yaz
      </button>
    </div>
  );
}

export default function TsKayitPublicPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [durumKarti, setDurumKarti] = useState(null);
  const [submittedBasvuru, setSubmittedBasvuru] = useState(null);

  const [servisler, setServisler] = useState([]);
  const [servisYukleniyor, setServisYukleniyor] = useState(false);
  const [servisHata, setServisHata] = useState('');
  const [servisFiltre, setServisFiltre] = useState('');
  const [seciliTs, setSeciliTs] = useState(null);

  const [kullaniciAdi, setKullaniciAdi] = useState('');
  const [kullaniciAdiMesaj, setKullaniciAdiMesaj] = useState('');
  const [kullaniciAdiOk, setKullaniciAdiOk] = useState(null);
  const [kullaniciAdiKontrol, setKullaniciAdiKontrol] = useState(false);

  const [sifre, setSifre] = useState('');
  const [sifreTekrar, setSifreTekrar] = useState('');
  const [sifreGoster, setSifreGoster] = useState(false);

  const kullaniciAdiKurallar = useMemo(() => kurallariDegerlendir(KULLANICI_ADI_KURALLARI, kullaniciAdi), [kullaniciAdi]);
  const kullaniciAdiKuralOk = kullaniciAdiKurallar.every((k) => k.passed);
  const sifreKurallar = useMemo(() => kurallariDegerlendir(SIFRE_KURALLARI, sifre), [sifre]);
  const sifreKuralOk = sifreKurallar.every((k) => k.passed);
  const sifrelerUyusuyor = sifreTekrar.length > 0 && sifre === sifreTekrar;

  const canStep1 = !!seciliTs;
  const canStep2 = kullaniciAdiKuralOk && kullaniciAdiOk === true && !kullaniciAdiKontrol;
  const canSubmit = canStep1 && canStep2 && sifreKuralOk && sifrelerUyusuyor && !submitLoading;

  const syncOturum = useCallback(async (basvuruId) => {
    const res = await fetchTsKayitOturum(token, basvuruId);
    const veri = res?.veri;
    setSubmitted(true);
    setDurumKarti(veri?.durumKarti || { durum: 'onay_bekliyor' });
    setSubmittedBasvuru(veri?.basvuru || null);
    return veri;
  }, [token]);

  const loadServisler = useCallback(async () => {
    setServisYukleniyor(true);
    setServisHata('');
    try {
      const res = await searchTsKayitTeknikServisler(token, '');
      setServisler(res?.veri?.items || []);
    } catch (err) {
      setServisHata(err?.message || 'Servis listesi yüklenemedi');
    } finally {
      setServisYukleniyor(false);
    }
  }, [token]);

  const loadForm = useCallback(async () => {
    if (!token) {
      setError('Geçersiz link');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');

    const oturum = getStoredOturum(token);
    if (oturum?.basvuruId) {
      try {
        await syncOturum(oturum.basvuruId);
        setLoading(false);
        return;
      } catch {
        clearOturum(token);
      }
    }

    try {
      await fetchTsKayitForm(token);
      setSubmitted(false);
      loadServisler();
    } catch (err) {
      setError(err?.message || 'Form yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [token, syncOturum, loadServisler]);

  useEffect(() => {
    loadForm();
  }, [loadForm]);

  // Kullanıcı adı müsaitlik kontrolü: yalnızca kurallar sağlanınca, tek istek (debounce)
  useEffect(() => {
    if (!kullaniciAdiKuralOk) {
      setKullaniciAdiOk(null);
      setKullaniciAdiMesaj('');
      setKullaniciAdiKontrol(false);
      return undefined;
    }
    setKullaniciAdiKontrol(true);
    let iptal = false;
    const t = setTimeout(async () => {
      try {
        const res = await checkTsKayitKullaniciAdi(token, kullaniciAdi);
        if (iptal) return;
        const musait = res?.veri?.musait === true;
        setKullaniciAdiOk(musait);
        setKullaniciAdiMesaj(musait ? '' : (res?.veri?.mesaj || 'Bu kullanıcı adı alınmış'));
      } catch (err) {
        if (iptal) return;
        setKullaniciAdiOk(false);
        setKullaniciAdiMesaj(err?.message || 'Kontrol yapılamadı');
      } finally {
        if (!iptal) setKullaniciAdiKontrol(false);
      }
    }, 500);
    return () => {
      iptal = true;
      clearTimeout(t);
    };
  }, [kullaniciAdi, kullaniciAdiKuralOk, token]);

  const filtreliServisler = useMemo(() => {
    const q = normalizeAra(servisFiltre);
    if (!q) return servisler;
    return servisler.filter((ts) => {
      if (ts.kod != null && String(ts.kod) === q) return true;
      return normalizeAra(`${ts.ad} ${ts.ilAdi || ''} ${ts.ilceAdi || ''}`).includes(q);
    });
  }, [servisler, servisFiltre]);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setFormError('');
    setSubmitLoading(true);
    try {
      const res = await submitTsKayitBasvuru(token, {
        kullaniciAdi,
        sifre,
        sifreTekrar,
        techServiceId: seciliTs.id,
      });
      const basvuru = res?.veri?.basvuru || null;
      const basvuruId = res?.veri?.basvuruId || basvuru?.id || basvuru?.basvuruId;
      if (basvuruId) saveOturum(token, basvuruId);
      setSubmitted(true);
      setDurumKarti(res?.veri?.durumKarti || { durum: 'onay_bekliyor' });
      setSubmittedBasvuru(basvuru);
    } catch (err) {
      setFormError(err?.message || 'Başvuru gönderilemedi');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleYeniKayit = useCallback(() => {
    clearOturum(token);
    setStep(1);
    setSeciliTs(null);
    setServisFiltre('');
    setKullaniciAdi('');
    setSifre('');
    setSifreTekrar('');
    setFormError('');
    setSubmitted(false);
    setDurumKarti(null);
    setSubmittedBasvuru(null);
    loadForm();
  }, [token, loadForm]);

  if (loading) {
    return (
      <TsKayitShell>
        <div className="ts-kayit-loading">Yükleniyor...</div>
      </TsKayitShell>
    );
  }

  if (error) {
    return (
      <TsKayitShell>
        <div className="ts-kayit-error">{error}</div>
        <YardimAlani token={token} metin="Linkle ilgili bir sorun olduğunu düşünüyorsanız sistem yöneticisine haber verin." />
      </TsKayitShell>
    );
  }

  if (submitted) {
    return (
      <TsKayitShell showYeniKayit onYeniKayit={handleYeniKayit}>
        <DurumKarti durumKarti={durumKarti} basvuru={submittedBasvuru} />
        <YardimAlani
          token={token}
          baglam={{
            basvuruId: submittedBasvuru?.id || submittedBasvuru?.basvuruId,
            techServiceId: submittedBasvuru?.techService?.id,
            kullaniciAdi: submittedBasvuru?.kullaniciAdi,
          }}
        />
      </TsKayitShell>
    );
  }

  return (
    <TsKayitShell>
      <div className="ts-kayit-adim-bilgi">
        <span>Adım {step} / 3</span>
        <strong>{ADIM_BASLIKLARI[step - 1]}</strong>
      </div>
      <div className="ts-kayit-steps">
        {[1, 2, 3].map((n) => (
          <div key={n} className={`ts-kayit-step-dot${step === n ? ' active' : ''}${step > n ? ' done' : ''}`} />
        ))}
      </div>

      {step === 1 && (
        <>
          <div className="ts-kayit-field">
            <label htmlFor="ts-filtre">Teknik servisiniz</label>
            <input
              id="ts-filtre"
              type="search"
              value={servisFiltre}
              onChange={(e) => setServisFiltre(e.target.value)}
              placeholder="Listeden seçin veya servis adı / il yazın"
              autoComplete="off"
            />
            {servisYukleniyor && <div className="ts-kayit-hint">Servisler yükleniyor...</div>}
            {servisHata && (
              <div className="ts-kayit-hint err">
                {servisHata}{' '}
                <button type="button" className="ts-kayit-link-btn" onClick={loadServisler}>Tekrar dene</button>
              </div>
            )}
            {!servisYukleniyor && !servisHata && (
              <div className="ts-kayit-ts-list ts-kayit-ts-list-tam" role="listbox" aria-label="Teknik servisler">
                {filtreliServisler.length === 0 && (
                  <div className="ts-kayit-ts-bos">Sonuç bulunamadı.</div>
                )}
                {filtreliServisler.map((ts) => {
                  const bekliyor = ts.durum === 'basvuru_bekliyor';
                  return (
                    <button
                      key={ts.id}
                      type="button"
                      role="option"
                      aria-selected={seciliTs?.id === ts.id}
                      disabled={bekliyor}
                      className={`ts-kayit-ts-item${seciliTs?.id === ts.id ? ' selected' : ''}${bekliyor ? ' disabled' : ''}`}
                      onClick={() => !bekliyor && setSeciliTs(ts)}
                    >
                      <strong>{ts.ad}</strong>
                      <small>
                        {[ts.ilAdi, ts.ilceAdi].filter(Boolean).join(' / ')}
                        {bekliyor ? ' · Onay bekleyen başvurusu var' : ''}
                      </small>
                    </button>
                  );
                })}
              </div>
            )}
            {seciliTs && (
              <div className="ts-kayit-selected-ts">
                Seçili servis: <strong>{seciliTs.ad}</strong>
              </div>
            )}
          </div>
          <div className="ts-kayit-actions">
            <button type="button" className="ts-kayit-btn-primary" disabled={!canStep1} onClick={() => setStep(2)}>
              Devam
            </button>
          </div>
          <YardimAlani token={token} metin="Servisinizi listede bulamıyor musunuz?" />
        </>
      )}

      {step === 2 && (
        <>
          <div className="ts-kayit-selected-ts ts-kayit-ust-ozet">
            Servis: <strong>{seciliTs?.ad}</strong>
          </div>
          <div className="ts-kayit-field">
            <label htmlFor="ts-kullanici-adi">Kullanıcı adı</label>
            <input
              id="ts-kullanici-adi"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={KULLANICI_ADI_MAX}
              value={kullaniciAdi}
              onChange={(e) => setKullaniciAdi(e.target.value.replace(/\s/g, '').toLowerCase())}
              placeholder="ornek: ankara_servis"
            />
            <KuralListesi kurallar={kullaniciAdiKurallar} dolu={kullaniciAdi.length > 0} />
            {kullaniciAdiKontrol && <div className="ts-kayit-hint">Kontrol ediliyor...</div>}
            {!kullaniciAdiKontrol && kullaniciAdiMesaj && <div className="ts-kayit-hint err">{kullaniciAdiMesaj}</div>}
            {!kullaniciAdiKontrol && kullaniciAdiOk === true && <div className="ts-kayit-hint ok">Bu kullanıcı adı kullanılabilir</div>}
            <div className="ts-kayit-hint">Uygulamaya girişte bu kullanıcı adını kullanacaksınız. Not alın.</div>
          </div>
          <div className="ts-kayit-actions">
            <button type="button" className="ts-kayit-btn-secondary" onClick={() => setStep(1)}>Geri</button>
            <button type="button" className="ts-kayit-btn-primary" disabled={!canStep2} onClick={() => setStep(3)}>
              Devam
            </button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <div className="ts-kayit-selected-ts ts-kayit-ust-ozet">
            <strong>{seciliTs?.ad}</strong> · {kullaniciAdi}
          </div>
          <div className="ts-kayit-field">
            <label htmlFor="ts-sifre">Şifre</label>
            <input
              id="ts-sifre"
              type={sifreGoster ? 'text' : 'password'}
              autoComplete="new-password"
              autoCapitalize="none"
              autoCorrect="off"
              value={sifre}
              onChange={(e) => setSifre(e.target.value)}
              maxLength={32}
            />
            <KuralListesi kurallar={sifreKurallar} dolu={sifre.length > 0} />
          </div>
          <div className="ts-kayit-field">
            <label htmlFor="ts-sifre-tekrar">Şifre tekrar</label>
            <input
              id="ts-sifre-tekrar"
              type={sifreGoster ? 'text' : 'password'}
              autoComplete="new-password"
              autoCapitalize="none"
              autoCorrect="off"
              value={sifreTekrar}
              onChange={(e) => setSifreTekrar(e.target.value)}
              maxLength={32}
            />
            {sifreTekrar.length > 0 && !sifrelerUyusuyor && <div className="ts-kayit-hint err">Şifreler eşleşmiyor</div>}
            {sifrelerUyusuyor && <div className="ts-kayit-hint ok">Şifreler eşleşiyor</div>}
          </div>
          <label className="ts-kayit-checkbox">
            <input type="checkbox" checked={sifreGoster} onChange={(e) => setSifreGoster(e.target.checked)} />
            Şifreyi göster
          </label>
          {formError && <div className="ts-kayit-hint err ts-kayit-form-hata">{formError}</div>}
          <div className="ts-kayit-actions">
            <button type="button" className="ts-kayit-btn-secondary" onClick={() => setStep(2)} disabled={submitLoading}>Geri</button>
            <button type="button" className="ts-kayit-btn-primary" disabled={!canSubmit} onClick={handleSubmit}>
              {submitLoading ? 'Gönderiliyor...' : 'Kaydı tamamla'}
            </button>
          </div>
          {formError && (
            <YardimAlani
              token={token}
              baglam={{ techServiceId: seciliTs?.id, kullaniciAdi }}
            />
          )}
        </>
      )}
    </TsKayitShell>
  );
}
